import { message } from 'antd';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike } from '@/utils/infrastructure';

const TOKEN_STORAGE_KEY = `x-${window.tabID}-entity-token`;

const readCachedToken = () => {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    const parsed = isJsonLike(raw) ? JSON.parse(raw) : null;
    return parsed?.token ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedToken = (tokenData) => {
  if (!tokenData) return;
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  } catch {
    // Silent fail
  }
};

const api = bindServices('entityBuilder');

const initialState = {
  currentEntity: null,
  fields: [],
  // Full `configurations` envelope returned by `dynamicEntity/config/version`.
  // Stored so panels (e.g. AccessControlDrawer) can read whole-block elements
  // like `accessControl` without an extra round trip.
  configurations: null,
  generatedToken: null,
  versions: [],
  dependencies: [],
  dependents: [],
  loading: false,
  initError: null,
};

const Model = {
  namespace: 'entityBuilder',
  state: initialState,

  effects: {
    *initBuilder({ payload }, { call, put }) {
      const { entityId } = payload;
      yield put({ type: 'setLoading', payload: true });

      let entity;
      try {
        entity = yield call(api.getById, { route: 'dynamicEntity', id: entityId });
        if (!entity || !entity.id) throw new Error('not-found');
        yield put({ type: 'saveEntity', payload: entity });
        yield put({ type: 'entity/queryEntity', payload: entity });
      } catch (e) {
        yield put({ type: 'setLoading', payload: false });
        yield put({ type: 'initFailed', payload: 'entity' });
        return;
      }

      try {
        const versionsRes = yield call(api.apiRoute, {
          route: `dynamicEntity/config/${entityId}/versions`,
          params: {
            select: 'id,number,current,published,publisher,publishedDate,creator,created',
            orderBy: 'number desc',
            limit: 50,
          },
        });
        const items = versionsRes?.items || versionsRes?.data?.items || [];
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // Versions fetch failed — non-critical
      }

      try {
        const ids = [entityId, CoreEnums.AccessMode.Write].filter(Boolean).join('/');
        const tokenRes = yield call(api.apiRoute, {
          route: `dynamicEntity/config/generateToken/${ids}`,
          method: 'post',
        });

        if (!tokenRes) throw new Error('not-found');
        writeCachedToken(tokenRes);

        yield put({ type: 'saveToken', payload: tokenRes });
        yield put({ type: 'entity/queryAccessMode', payload: tokenRes });

        if (tokenRes?.entityId && tokenRes?.versionId) {
          yield put({
            type: 'fetchFields',
            payload: {
              entityId: tokenRes.entityId,
              versionId: tokenRes.versionId,
              token: tokenRes.token,
            },
          });
        }
      } catch (e) {
        yield put({ type: 'setLoading', payload: false });
        yield put({ type: 'initFailed', payload: 'token' });
        return;
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchVersions({ payload }, { call, put, select }) {
      try {
        const entityId =
          payload?.entityId || (yield select((s) => s.entityBuilder.currentEntity?.id));
        if (!entityId) return;
        const res = yield call(api.apiRoute, {
          route: `dynamicEntity/config/${entityId}/versions`,
          params: {
            select: 'id,number,current,published,publisher,publishedDate,creator,created',
            orderBy: 'number desc',
            limit: 50,
          },
        });
        const items = res?.items || res?.data?.items || [];
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // Error handled globally
      }
    },

    *changeVersion({ payload }, { call, put, select }) {
      const { versionId } = payload;
      if (!versionId) return;

      const entityId = yield select((s) => s.entityBuilder.currentEntity?.id);
      const versions = yield select((s) => s.entityBuilder.versions);
      if (!entityId) return;

      const selected = versions.find((v) => v.id === versionId);
      const mode = selected?.published ? CoreEnums.AccessMode.ReadOnly : CoreEnums.AccessMode.Write;

      yield put({ type: 'setLoading', payload: true });

      try {
        const tokenRes = yield call(api.apiRoute, {
          route: `dynamicEntity/config/generateToken/${entityId}/${mode}/${versionId}`,
          method: 'post',
        });

        writeCachedToken(tokenRes);
        yield put({ type: 'saveToken', payload: tokenRes });
        yield put({ type: 'entity/queryAccessMode', payload: tokenRes });

        if (tokenRes?.entityId && tokenRes?.versionId) {
          yield put({
            type: 'fetchFields',
            payload: {
              entityId: tokenRes.entityId,
              versionId: tokenRes.versionId,
              token: tokenRes.token,
            },
          });
        }
      } catch (e) {
        // Error handled globally
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchFields({ payload }, { call, put, select }) {
      try {
        const token = payload.token || (yield select((s) => s.entityBuilder.generatedToken?.token));
        const entityId =
          payload.entityId || (yield select((s) => s.entityBuilder.generatedToken?.entityId));
        const versionId =
          payload.versionId || (yield select((s) => s.entityBuilder.generatedToken?.versionId));

        const res = yield call(api.getAll, {
          route: `dynamicEntity/config/version/${entityId}/${versionId}`,
          params: { token },
        });

        const config = res?.configurations || {};
        yield put({ type: 'saveFields', payload: config.fields || [] });
        yield put({ type: 'saveConfigurations', payload: config });
      } catch (e) {
        yield put({ type: 'saveFields', payload: [] });
        yield put({ type: 'saveConfigurations', payload: null });
      }
    },

    // Generic versioned-builder append for whole-block element types (e.g.
    // `entity-access-control`). Mirrors the list-builder/agent-builder pattern.
    // Caller is responsible for refreshing the config snapshot (fetchFields)
    // after the append succeeds so panels re-baseline against the new state.
    *appendElement({ payload }, { call, select }) {
      const { elementType, data } = payload || {};
      const { entityId, versionId, token, mode } =
        (yield select((s) => s.entityBuilder.generatedToken)) || {};
      if (mode !== CoreEnums.AccessMode.Write) return null;
      if (!entityId || !versionId || !token) return null;
      try {
        return yield call(api.apiRoute, {
          route: `dynamicEntity/config/append/${entityId}/${versionId}`,
          params: { token },
          method: 'post',
          data: { elementType, data },
        });
      } catch (e) {
        return null;
      }
    },

    *fetchDependencies({ payload }, { call, put, select }) {
      try {
        const entityId =
          payload?.entityId || (yield select((s) => s.entityBuilder.currentEntity?.id));
        if (!entityId) return;
        const { versionId, token } = yield select((s) => s.entityBuilder.generatedToken || {});
        const vid = payload?.versionId || versionId;
        const route = vid
          ? `dynamicEntity/config/${entityId}/dependencies/${vid}`
          : `dynamicEntity/config/${entityId}/dependencies`;
        const res = yield call(api.apiRoute, { route, params: { limit: 1000, token } });
        yield put({ type: 'saveDependencies', payload: res?.items || res || [] });
      } catch (e) {
        yield put({ type: 'saveDependencies', payload: [] });
      }
    },

    *fetchDependents({ payload }, { call, put, select }) {
      try {
        const entityId =
          payload?.entityId || (yield select((s) => s.entityBuilder.currentEntity?.id));
        if (!entityId) return;
        const res = yield call(api.apiRoute, {
          route: `dynamicEntity/config/${entityId}/dependents`,
          params: { limit: 1000 },
        });
        yield put({ type: 'saveDependents', payload: res?.items || res || [] });
      } catch (e) {
        yield put({ type: 'saveDependents', payload: [] });
      }
    },

    *reorderFields({ payload }, { call, put, select }) {
      const { orderedIds } = payload;
      if (!orderedIds?.length) return;

      const { entityId, versionId, token } = yield select(
        (s) => s.entityBuilder.generatedToken || {},
      );
      if (!entityId || !versionId || !token) return;

      const fields = yield select((s) => s.entityBuilder.fields);
      const idOrder = {};
      orderedIds.forEach((id, i) => {
        idOrder[id] = i;
      });
      const reordered = [...fields]
        .sort((a, b) => (idOrder[a.id] ?? 999) - (idOrder[b.id] ?? 999))
        .map((f, i) => ({ ...f, order: i }));
      yield put({ type: 'saveFields', payload: reordered });

      try {
        yield call(api.apiRoute, {
          route: `dynamicEntity/config/modify/${entityId}/${versionId}`,
          params: { token },
          method: 'post',
          data: { elementType: 'form-fields-order', data: { orderedIds } },
        });
      } catch (e) {
        yield put({ type: 'saveFields', payload: fields });
      }
    },

    *refreshEntity(_, { call, put, select }) {
      try {
        const entityId = yield select((s) => s.entityBuilder.currentEntity?.id);
        if (!entityId) return;
        const entity = yield call(api.getById, { route: 'dynamicEntity', id: entityId });
        yield put({ type: 'saveEntity', payload: entity });
      } catch (e) {
        // Error handled globally
      }
    },

    *publishEntity(_, { call, put, select }) {
      try {
        const { entityId, versionId, token, mode } = yield select(
          (s) => s.entityBuilder.generatedToken,
        );
        if (mode !== CoreEnums.AccessMode.Write) return;

        const raw = yield call(api.apiRoute, {
          route: `dynamicEntity/config/publish/${entityId}/${versionId}`,
          params: { token },
          method: 'post',
        });
        const res = raw?.data || raw;

        const translate = getIntl().formatMessage;
        message.success(res?.message || translate({ id: 'entityBuilder.publish.success' }));

        // Request a fresh Write token for the new draft created after publish.
        // The backend may need a moment to provision the new draft, so retry once.
        let writeToken = yield call(api.apiRoute, {
          route: `dynamicEntity/config/generateToken/${entityId}/${CoreEnums.AccessMode.Write}`,
          method: 'post',
        });

        if (
          !writeToken ||
          writeToken.versionId === versionId ||
          writeToken.mode !== CoreEnums.AccessMode.Write
        ) {
          yield call(() => new Promise((r) => setTimeout(r, 1500)));
          writeToken = yield call(api.apiRoute, {
            route: `dynamicEntity/config/generateToken/${entityId}/${CoreEnums.AccessMode.Write}`,
            method: 'post',
          });
        }

        if (writeToken) {
          writeCachedToken(writeToken);
          yield put({ type: 'saveToken', payload: writeToken });
          yield put({ type: 'entity/queryAccessMode', payload: writeToken });

          yield put({ type: 'fetchVersions', payload: { entityId } });
          yield put({
            type: 'fetchFields',
            payload: {
              entityId: writeToken.entityId,
              versionId: writeToken.versionId,
              token: writeToken.token,
            },
          });
          yield put({
            type: 'fetchDependencies',
            payload: { entityId, versionId: writeToken.versionId },
          });
          yield put({ type: 'fetchDependents', payload: { entityId } });
          yield put({ type: 'refreshEntity' });
        }
      } catch (e) {
        const errorMsg = e?.message || e?.data?.message || e?.Message;
        if (errorMsg) message.error(errorMsg);
      }
    },
  },

  reducers: {
    saveEntity(state, { payload }) {
      return { ...state, currentEntity: payload };
    },
    saveToken(state, { payload }) {
      return { ...state, generatedToken: payload };
    },
    saveFields(state, { payload }) {
      return { ...state, fields: payload };
    },
    saveConfigurations(state, { payload }) {
      return { ...state, configurations: payload };
    },
    saveVersions(state, { payload }) {
      return { ...state, versions: payload };
    },
    saveDependencies(state, { payload }) {
      return { ...state, dependencies: payload };
    },
    saveDependents(state, { payload }) {
      return { ...state, dependents: payload };
    },
    setLoading(state, { payload }) {
      return { ...state, loading: payload };
    },
    initFailed(state, { payload }) {
      return { ...state, initError: payload };
    },
    resetBuilder() {
      return initialState;
    },
  },
};

export default Model;
